import React, { useContext } from 'react'
import { CookieConsumer, CookieSetter } from '#src/context/cookies'
import {
  DepartmentContextFragment as DepartmentContextFragmentType,
  DepartmentContextFragmentDoc
} from './DepartmentFragment.generated'
import { filter } from 'graphql-anywhere'
import { DepartmentType } from '#src/generated/types'

/**
 * The DepartmentProvider exposes the current department, as well as a setter.
 */
export type MaybeDepartment = DepartmentContextFragmentType | null
export type DepartmentContextType = {
  department: MaybeDepartment
  setDepartment?: (department: MaybeDepartment) => void
}

// Create the underlying provider and consumer
const DepartmentContext = React.createContext<DepartmentContextType>({
  department: null
})

const { Provider, Consumer } = DepartmentContext

// A wrapper provider acts as the state container and manager
interface DepartmentProviderProps {
  defaultDepartment: MaybeDepartment
}

interface DepartmentProviderState {
  department: MaybeDepartment
}

class DepartmentProvider extends React.Component<DepartmentProviderProps, DepartmentProviderState> {
  public constructor(props: DepartmentProviderProps) {
    super(props)

    this.state = {
      department: this.props.defaultDepartment
    }

    this.setDepartment = this.setDepartment.bind(this)
  }

  public setDepartment(setCookie: CookieSetter, department: MaybeDepartment) {
    if (!department) return

    // Yachts has been deactivated, but some pages are still live during the transition.
    // During this trantsition, block being able to set yacht charters so that we don't need to
    // filter components rendering based on the department context.
    if (department.type !== DepartmentType.YACHT_CHARTERS) {
      const filteredDepartment = filter(DepartmentContextFragmentDoc, department)

      setCookie('curated_department', JSON.stringify(filteredDepartment), {
        path: '/'
      })

      this.setState({
        department
      })
    }
  }

  public render() {
    return (
      <CookieConsumer>
        {cookies => {
          return (
            <Provider
              value={{
                department: this.state.department,
                setDepartment: cookies
                  ? this.setDepartment.bind(this, cookies.setCookie)
                  : undefined
              }}
            >
              {this.props.children}
            </Provider>
          )
        }}
      </CookieConsumer>
    )
  }
}

const DepartmentProviderInjector: React.FC<{}> = ({ children }) => {
  return (
    <CookieConsumer>
      {cookies => {
        const departmentCookieValue = cookies && cookies.getCookie('curated_department')
        const defaultDepartment = departmentCookieValue
          ? (JSON.parse(departmentCookieValue) as MaybeDepartment)
          : null

        return (
          <DepartmentProvider
            defaultDepartment={
              // See comment above why yachts is checked here
              defaultDepartment?.type !== DepartmentType.YACHT_CHARTERS ? defaultDepartment : null
            }
          >
            {children}
          </DepartmentProvider>
        )
      }}
    </CookieConsumer>
  )
}

const useDepartmentContext = () => {
  const departmentContext = useContext(DepartmentContext)

  if (!DepartmentContext) {
    throw new Error('Invoked DepartmentContext outside of provider')
  }

  return departmentContext
}

// Export with clean names
export {
  DepartmentProviderInjector as DepartmentProvider,
  Consumer as DepartmentConsumer,
  DepartmentContext,
  useDepartmentContext
}
